import React, { lazy, ReactNode, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Home } from './pages/Home';
import { PublicLanding } from './pages/PublicLanding';
import { PublicForm } from './pages/PublicForm';
import { ThankYou } from './pages/ThankYou';
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { Toaster } from './components/ui/Toast';

/**
 * La zona privada (CRM, constructor, arrastrar y soltar, administración) se
 * carga bajo demanda: un visitante que solo abre una landing pública nunca
 * descarga ese código.
 */
const AppLayout = lazy(() => import('./layouts/AppLayout').then(m => ({
  default: m.AppLayout
})));
const Dashboard = lazy(() => import('./pages/app/Dashboard').then(m => ({
  default: m.Dashboard
})));
const Leads = lazy(() => import('./pages/app/Leads').then(m => ({
  default: m.Leads
})));
const LeadDetail = lazy(() => import('./pages/app/LeadDetail').then(m => ({
  default: m.LeadDetail
})));
const Pipeline = lazy(() => import('./pages/app/Pipeline').then(m => ({
  default: m.Pipeline
})));
const Trash = lazy(() => import('./pages/app/Trash').then(m => ({
  default: m.Trash
})));
const Forms = lazy(() => import('./pages/app/Forms').then(m => ({
  default: m.Forms
})));
const FormEditor = lazy(() => import('./pages/app/FormEditor').then(m => ({
  default: m.FormEditor
})));
const Landings = lazy(() => import('./pages/app/Landings').then(m => ({
  default: m.Landings
})));
const LandingEditor = lazy(() => import('./pages/app/LandingEditor').then(m => ({
  default: m.LandingEditor
})));
const Team = lazy(() => import('./pages/app/Team').then(m => ({
  default: m.Team
})));
const Settings = lazy(() => import('./pages/app/Settings').then(m => ({
  default: m.Settings
})));
const Integrations = lazy(() => import('./pages/app/Integrations').then(m => ({
  default: m.Integrations
})));

/** Indicador de carga compartido mientras llega el código de la zona privada. */
function Loading() {
  return <div className="flex min-h-screen w-full items-center justify-center bg-background" aria-busy="true" data-id="element-0" data-mp="d4nvzht0nm2x">
      <span className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" data-id="element-1" data-mp="85uj9rljzz3t" />
      <span className="sr-only" data-id="element-2" data-mp="3gl4sdjnv24l3">Cargando tu espacio de trabajo…</span>
    </div>;
}

/** Protección de la zona privada con la sesión real de Supabase. */
function RequireAuth({
  children
}: {
  children: ReactNode;
}) {
  const {
    session,
    restoring
  } = useAuth();
  const location = useLocation();
  if (restoring) return <Loading data-id="element-3" data-mp="3ljg2zwlzcj4d" />;
  if (!session) {
    return <Navigate to="/login" state={{
      from: location.pathname
    }} replace data-id="element-4" data-mp="3liedwo84pw3n" />;
  }
  return <>{children}</>;
}
export function App() {
  return <BrowserRouter data-id="element-5" data-mp="2swbpt4cqgyhi">
      <AuthProvider data-id="element-6" data-mp="2sv0pjeihy8v5">
        <Routes data-id="element-7" data-mp="2svjwk06lzcue">
          {/* Zona pública */}
          <Route path="/" element={<Home data-id="element-9" data-mp="18cq41f9j46qa" />} data-id="element-8" data-mp="18d9eyc0rhuut" />
          <Route path="/landing/:slug" element={<PublicLanding data-id="element-11" data-mp="1d4hqbdlnjqam" />} data-id="element-10" data-mp="18cgf7v0805m6" />
          <Route path="/formulario/:slug" element={<PublicForm data-id="element-13" data-mp="1d6kmyghb8hob" />} data-id="element-12" data-mp="1d5jbiawdtt62" />
          <Route path="/gracias/:slug" element={<ThankYou data-id="element-15" data-mp="ygmxlx7n9lxj" />} data-id="element-14" data-mp="yfv9f7gdmqha" />
          <Route path="/login" element={<Login data-id="element-17" data-mp="13eoeqclifb6d" />} data-id="element-16" data-mp="yheq2z5y33jl" />
          <Route path="/registro" element={<Register data-id="element-19" data-mp="13f7ubmnotnh8" />} data-id="element-18" data-mp="13dws3r9w4ns8" />
          <Route path="/recuperar-contrasena" element={<ForgotPassword data-id="element-21" data-mp="1r3zim2y5ploq" />} data-id="element-20" data-mp="13cbx60jnz2tu" />

          {/* Zona privada */}
          <Route path="/app" element={<RequireAuth data-id="element-23" data-mp="1w3vj3s7nauzj">
                <Suspense fallback={<Loading data-id="element-25" data-mp="1w2kfpti43p8y" />} data-id="element-24" data-mp="1w20wm9gq6utc">
                  <AppLayout data-id="element-26" data-mp="1i3ulqmqla5ot" />
                </Suspense>
              </RequireAuth>} data-id="element-22" data-mp="1w3c34h1k2go5">
            <Route index element={<Navigate to="/app/dashboard" replace data-id="element-28" data-mp="1m4wc0e2j37en" />} data-id="element-27" data-mp="1i5x7gukplcnq" />
            <Route path="dashboard" element={<Suspense fallback={<Loading data-id="element-31" data-mp="6nw6g14tinf5" />} data-id="element-30" data-mp="6ofemq4o1xfz"><Dashboard data-id="element-32" data-mp="6ncnqi7cfv02" /></Suspense>} data-id="element-29" data-mp="1m3cvcmcymucc" />
            <Route path="leads" element={<Suspense fallback={<Loading data-id="element-35" data-mp="aq9bsofnkjbe" />} data-id="element-34" data-mp="apr6l2b0iurc"><Leads data-id="element-36" data-mp="ar28sxot7ugc" /></Suspense>} data-id="element-33" data-mp="6qie00rpl2xd" />
            <Route path="leads/:id" element={<Suspense fallback={<Loading data-id="element-39" data-mp="qxf7hpbdb1hr" />} data-id="element-38" data-mp="qwvpy9pmqf49"><LeadDetail data-id="element-40" data-mp="qy72axx54v6z" /></Suspense>} data-id="element-37" data-mp="anwk3jk3eul5" />
            <Route path="pipeline" element={<Suspense fallback={<Loading data-id="element-43" data-mp="m5dzwgp59yy2" />} data-id="element-42" data-mp="m5x6j19ch0ws"><Pipeline data-id="element-44" data-mp="m5nfdackcnpi" /></Suspense>} data-id="element-41" data-mp="qz8dd2e5phop" />
            <Route path="papelera" element={<Suspense fallback={<Loading data-id="element-47" data-mp="h8drhz4tok3s" />} data-id="element-46" data-mp="h6szoh9w7f9m"><Trash data-id="element-48" data-mp="h83ys3se7yue" /></Suspense>} data-id="element-45" data-mp="m4m3j3nq3x6q" />
            <Route path="formularios" element={<Suspense fallback={<Loading data-id="element-51" data-mp="c9kee7tg6v45" />} data-id="element-50" data-mp="c90vans2a0oj"><Forms data-id="element-52" data-mp="c9u5jyq8b8bf" /></Suspense>} data-id="element-49" data-mp="h4zoqps8y8vt" />
            <Route path="formularios/nuevo" element={<Suspense fallback={<Loading data-id="element-55" data-mp="19vhq9mdbo274" />} data-id="element-54" data-mp="19uyjn1t4h08e"><Forms data-id="element-56" data-mp="19v8a0qi2x9em" /></Suspense>} data-id="element-53" data-mp="19vzvv9lvjsrp" />
            <Route path="formularios/:id" element={<Suspense fallback={<Loading data-id="element-59" data-mp="14xpzwwsbso51" />} data-id="element-58" data-mp="14y854iwyucp3"><FormEditor data-id="element-60" data-mp="14uu1768nlvfj" /></Suspense>} data-id="element-57" data-mp="14x6gfbn11row" />
            <Route path="landings" element={<Suspense fallback={<Loading data-id="element-63" data-mp="zw0p9i90mchh" />} data-id="element-62" data-mp="zwipsr2pncv7"><Landings data-id="element-64" data-mp="zzohme2q4sum" /></Suspense>} data-id="element-61" data-mp="zxamk581ngn2" />
            <Route path="landings/nueva" element={<Suspense fallback={<Loading data-id="element-67" data-mp="vybdq9j6yzqc" />} data-id="element-66" data-mp="vvp6yc44ko96"><Landings data-id="element-68" data-mp="3ko2riny8x0um" /></Suspense>} data-id="element-65" data-mp="vx0781yd3dvj" />
            <Route path="landings/:id/editor" element={<Suspense fallback={<Loading data-id="element-71" data-mp="3gmh8slz15sci" />} data-id="element-70" data-mp="3klyffiq70vj9"><LandingEditor data-id="element-72" data-mp="3gmpjq8blenl2" /></Suspense>} data-id="element-69" data-mp="3kp43cun35rde" />
            <Route path="equipo" element={<Suspense fallback={<Loading data-id="element-75" data-mp="1w4otab2j1isr" />} data-id="element-74" data-mp="3gotzpomrnd1p"><Team data-id="element-76" data-mp="1w4ykg1zb5w01" /></Suspense>} data-id="element-73" data-mp="3gkwd2t0zc3d2" />
            <Route path="configuracion" element={<Suspense fallback={<Loading data-id="element-79" data-mp="205qsirwg1uuq" />} data-id="element-78" data-mp="1w2lx19as6tfl"><Settings data-id="element-80" data-mp="2058qtfr0ioff" /></Suspense>} data-id="element-77" data-mp="1w5zxud3squkx" />
            <Route path="integraciones" element={<Suspense fallback={<Loading data-id="element-83" data-mp="254lf84xj83k4" />} data-id="element-82" data-mp="2553e79bkwvvq"><Integrations data-id="element-84" data-mp="25425vcly6nhp" /></Suspense>} data-id="element-81" data-mp="206a2nmfurey7" />
          </Route>

          <Route path="*" element={<Navigate to="/" replace data-id="element-86" data-mp="29wd1w4dkhp4z" />} data-id="element-85" data-mp="29vtkqpvqr4s0" />
        </Routes>
        {/* Confirmaciones de copiado, publicación y guardado */}
        <Toaster position="bottom-right" data-id="element-87" data-mp="2eu4uz1py7har" />
      </AuthProvider>
    </BrowserRouter>;
}